import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';

import { Formik, FormikProps } from 'formik';
import * as Yup from 'yup';

import { Camera } from '@components/app/DocumentSteps/components/Camera';
import { base64ToBlob, getImageFileFromBlob } from '@components/app/DocumentSteps/utils/helpers';
import { BoxedButton } from '@components/shared/BoxedButton/BoxedButton';
import { ClosePageIcon } from '@components/shared/customIcons/ClosePageIcon';
import { Loader } from '@components/shared/Loader/Loader';
import { useInjection } from '@context/inversify-context-provider';
import { OOStepsEnum } from '@features/oneOnboarding/interfaces';
import { OOUserDocumentPartModel } from '@features/oneOnboarding/models/UserDocumentPartModel';
import { FileUpload } from '@features/oneOnboardingAdmin/pages/NewCandidateProfilePage/DocumentDetails/DocumentPages/FileUpload';
import { getBlobData } from '@helpers/fileUpload.helper';
import { ChosenFileValidation, validateChosenFile } from '@helpers/fileUpload.helper';
import { getStepperComponent } from '@helpers/getStepperComponent';
import { OnboardingDocumentStatus } from '@helpers/OnboardingSteps';
import { Utils } from '@helpers/utils';
import { mutateUserFlow, useUploadLimitSize } from '@hooks/apiHooks';
import { TYPES } from '@libs/InversifyType';
import CloseIcon from '@mui/icons-material/Close';
import { LoadingButton } from '@mui/lab';
import { Box, Button, Dialog, Grid, IconButton, Typography, useTheme } from '@mui/material';
import { RootState } from '@store/rootReducer';
import { snackbarSlice } from '@store/slices/snackbar';
import { useQueryClient } from '@tanstack/react-query';

import { AamBackendApi } from '../../../../libs/aamBackendApi';
import { ReadOnlyDocumentInfo } from '../../components/ReadOnlyDocumentInfo';
import { OOFormControl } from '../../controls/FormControl';
import { controlModelToValidationObject, getDocumentStatus, parseValidationErrors } from '../../helpers';
import { useT } from '../../hooks/useT';
import { OOControlModel } from '../../models/ControlModel';
import { OODocumentationModel } from '../../models/DocumentationModel';
import { OODocumentModel } from '../../models/DocumentModel';
import { OODocumentPartModel } from '../../models/DocumentPartModel';
import { OOFlowWrapper } from '../../wrappers/FlowWrapper';
import OpenSdkCameraDisclaimer from '../DataCheckerUpload/OpenSdkCameraDisclaimer';
import ErrorPageDialog from '../ErrorPage/ErrorPageDialog';
import SuccessPageDialog from '../SuccessDialog/SuccessPageDialog';

import { ExampleDocument } from './ExampleDocument';
import { ImagePreview } from './ImagePreview';

interface UploadDocumentProps {
  stepLabel: string;
  stepName: string;
  document: OODocumentModel;
  setDocument: (doc?: OODocumentModel) => void;
  documentationModel: OODocumentationModel;
  setDocumentationModel: (doc?: OODocumentationModel) => void;
  documentationSelected?: (d: OODocumentationModel) => void;
  isLastDocumentToUpload?: boolean;
  callOnSubmit: (documentation?: OODocumentationModel) => Promise<void>;
  isVisibleReadOnly?: boolean;
  nextButtonClicked?: boolean;
  isLoading?: boolean;
  hideDocumentExamples?: boolean;
  isDisabledForCandidate?: boolean;
}

interface State {
  [key: string]: {
    file?: File;
    data?: string | ArrayBuffer | null;
  };
}

export interface SdkCapturedDocumentType {
  images: string[];
  meta: any[];
}

const dcDocumentParts = ['FRONT', 'BACK'];

export const UploadDocument: React.FC<UploadDocumentProps> = ({
  stepLabel,
  stepName,
  document,
  documentationModel,
  setDocument,
  setDocumentationModel,
  isLastDocumentToUpload,
  callOnSubmit,
  documentationSelected,
  isVisibleReadOnly = true,
  nextButtonClicked,
  hideDocumentExamples,
  isDisabledForCandidate,
  ...rest
}) => {
  const [initDataImage, setInitDataImage] = useState(true);
  const [cameraActive, setCameraActive] = useState(false);
  const [cameraPart, setCameraPart] = useState('');
  const [, setPartName] = useState('');
  const [fw, setFw] = useState<boolean>(false);
  const [state, setState] = useState<State>({});
  const [initialValues, setInitialValues] = useState<any>({});
  const [sizeError, setSizeError] = useState<boolean>(false);
  const [emptyFileError, setEmptyFileError] = useState<boolean>(false);
  const [typeError, setTypeError] = useState<boolean>(false);
  const [previewImageUrl, setPreviewImageUrl] = useState<any>({});
  const [isRemoving, setIsRemoving] = useState<boolean>(false);
  const [isFileReuploaded, setIsFileReuploaded] = useState(false);
  const [sdkSuccess, setSdkSuccess] = useState(false);
  const [sdkError, setSdkError] = useState<boolean>(false);
  const [sdkCapturedDocument, setSdkCapturedDocument] = useState<SdkCapturedDocumentType | null>(null);

  const { themeAdditionalData } = useSelector((state: RootState) => state.oneOnboarding);
  const aamBackendApi = useInjection<AamBackendApi>(TYPES.AamBackendApi);
  const store = useStore<RootState>();
  const dispatch = useDispatch();
  const documentation = useMemo(() => documentationModel, [documentationModel])!;
  const oneOnboarding = useSelector((state: RootState) => state.oneOnboarding)!;
  const theme = useTheme();
  const { t } = useT('documents', 'entry', 'candidate_recruiter');

  // For DC AutoCapture SDK
  const { sdkMode = '', dcAdditionalDocumentType = '', dcDocumentType = '', dcAllowedDocument = null } = document;

  const { clientOrganizationId, selectedConfigurationId, applicationId } = useSelector(
    (state: RootState) => state.oneOnboarding,
  );

  const { data: uploadFileSizeInfo } = useUploadLimitSize(clientOrganizationId!, selectedConfigurationId!);
  let sizeLimit = Number(uploadFileSizeInfo?.maxUploadedFileSizeMB);
  const documentPartsCount = document?.parts.length;
  if (uploadFileSizeInfo?.divideFileSizeByDocumentParts && sizeLimit && documentPartsCount > 1) {
    sizeLimit = sizeLimit / documentPartsCount;
    sizeLimit = Number.parseFloat(sizeLimit.toFixed(1));
  }

  const { userId } = useSelector((state: RootState) => state.authApp);
  const queryClient = useQueryClient();
  const { mutateAsync, isPending } = mutateUserFlow(
    clientOrganizationId!,
    selectedConfigurationId!,
    userId!,
    applicationId!,
    queryClient,
  );

  const flow = useMemo(() => {
    if (!oneOnboarding.flow) return null;
    return OOFlowWrapper.create(oneOnboarding.flow);
  }, [oneOnboarding]);

  const hasValidationStep = useMemo(() => {
    if (!flow) {
      return false;
    }
    const csIndex = flow.steps.findIndex((step) => step.name === oneOnboarding.step);

    for (const nextStep of flow.steps.slice(csIndex)) {
      if (nextStep.name.toLowerCase().includes('validation')) {
        return true;
      }
    }
    return false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const status = useMemo(() => {
    const result = getDocumentStatus(documentationModel, hasValidationStep, isDisabledForCandidate);
    return result.status;

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentationModel]);

  useEffect(() => {
    const hasControls = document?.controls;
    const isDocumentRejected = document?.userDocument?.additionalData?.status === OnboardingDocumentStatus.REJECTED;

    if (hasControls) {
      const getControlValue = (control: OOControlModel) => {
        if (
          isDocumentRejected &&
          control.name !== 'IDENTIFICATION_NUMBER' &&
          control.name !== 'DOCUMENT_NUMBER' &&
          control.name !== 'SOCIAL_SECURITY_NUMBER' &&
          control.name !== 'IBAN'
        ) {
          return state[control.name] ?? undefined;
        }
        return control.value ?? state[control.name];
      };

      const getInitialValues = (controls: OOControlModel[]) => {
        const controlValues = controls.reduce((acc, control) => {
          acc[control.name] = getControlValue(control);
          return acc;
        }, {} as any);

        Object.keys(state).forEach((partName) => {
          if (state[partName].data) {
            controlValues[partName] = state[partName].data;
          }
        });

        return controlValues;
      };

      setInitialValues(getInitialValues(document.controls));
    }
  }, [state, document?.controls, document?.userDocument?.additionalData?.status]);

  useEffect(() => {
    const downloadImage = async () => {
      if (document?.userDocument?.additionalData?.status !== OnboardingDocumentStatus.REJECTED) {
        try {
          let newState: any = {};
          const documentParts: OODocumentPartModel[] | OOUserDocumentPartModel[] = sdkMode
            ? document?.userDocument?.userDocumentParts ?? []
            : document?.parts ?? [];
          const results: any[] = await Promise.all(
            documentParts.map((part: any) => {
              const userDocumentId = sdkMode ? part?.userDocumentId : part?.userDocumentPart?.userDocumentId;
              if (userDocumentId) {
                const partURL = userDocumentId + '/' + part.name;
                return aamBackendApi.downloadDocumentWithFullResponse(
                  store.getState().tenant.tenantId,
                  store.getState().authApp.userId!,
                  partURL,
                );
              } else {
                return [];
              }
            }),
          );
          if (results.length > 0) {
            documentParts.forEach((part, i) => {
              const { data, headers } = results[i];
              if (results[i].data) {
                const file = getBlobData(data, headers['content-type'], part.name);
                const fileImage = getImageFileFromBlob(data);
                newState = {
                  ...newState,
                  [part.name]: { ...newState[part.name], file: fileImage, data: file.imageUrl, link: file.link },
                };
              }
            });

            setState({ ...state, ...newState });
          }
        } finally {
          setInitDataImage(false);
          setFw(true);
        }
      } else {
        setFw(true);
      }
    };

    if (initDataImage && document?.parts) {
      downloadImage();
    }
  }, [
    initDataImage,
    aamBackendApi,
    document?.parts,
    state,
    store,
    sdkMode,
    document?.userDocument?.additionalData?.status,
    document?.userDocument?.userDocumentParts,
  ]);

  const onRemoveDisabled = useMemo<boolean>(() => {
    return !(
      (status === OnboardingDocumentStatus.MISSING || status === OnboardingDocumentStatus.REVIEW) &&
      Object.keys(state).length
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  if (!documentation) {
    setDocument(undefined);
    setDocumentationModel(undefined);
    return <></>;
  }

  const allRequiredFilesUploaded = document.parts.filter((p) => p.isMandatory).every((p) => !!state[p.name]);

  const partValidations = document.parts.reduce((acc, p) => {
    if (p.isMandatory) {
      acc[p.name] = Yup.string().required(t('documents:validation.required'));
    } else {
      acc[p.name] = Yup.string();
    }
    return acc;
  }, {} as any);

  const validationSchema = Yup.object({
    ...partValidations,
    ...controlModelToValidationObject(document.controls, t, true, flow, 'documents'),
  });

  const validateFile = (file: File) => {
    const sizeLimitToMB = sizeLimit ? sizeLimit * 1000000 : 0;
    const fileValidation = validateChosenFile(store.getState().tenant.tenantId, file, {
      allowOnlyImages: false,
      maxSize: sizeLimitToMB,
    });
    return fileValidation;
  };

  const cameraPictureTaken = (blob: any, props: FormikProps<any>) => {
    Utils.consoleLog(`Camera picture taken for ${cameraPart}`);
    setCameraActive(false);
    const type = blob.type.split('/')[1];
    const file = new File([blob], `camera_${new Date().getTime()}.${type}`, { type: blob.type });
    const fileValidationResult = validateFile(file);
    if (!fileValidationResult.valid) {
      onInvalidFileChoose(fileValidationResult);
      return;
    }
    const reader = new FileReader();
    reader.onload = function (e) {
      props.setFieldValue(cameraPart, e.target!.result);
      props.validateField(cameraPart);
      document.controls.forEach((c) => {
        if (props.values[c.name]) {
          state[c.name] = props.values[c.name];
        }
      });
      setState({ ...state, [cameraPart]: { ...state[cameraPart], file, data: e.target!.result } });
    };
    reader.readAsDataURL(file);
  };

  const onInvalidFileChoose = (validation: ChosenFileValidation) => {
    if (!validation.valid) {
      setTypeError(validation.typeError);
      setSizeError(validation.sizeError);
      setEmptyFileError(validation.emptyFileError);
    }
  };

  const onInvalidFilePopupClose = () => {
    setSizeError(false);
    setTypeError(false);
    setEmptyFileError(false);
  };

  const onRemoveDocuments = async () => {
    setState({});
    setIsRemoving(true);
    try {
      const data = { currentStep: stepName, currentPage: document.name };
      await aamBackendApi.removeDocument(
        clientOrganizationId!,
        selectedConfigurationId!,
        userId!,
        applicationId!,
        data,
      );
    } finally {
      setIsRemoving(false);
    }
  };

  const isReadOnly = document.userDocument?.isReadOnly;
  const areControlsVisible = (isReadOnly && isFileReuploaded) || !isReadOnly;

  const getSendData = (stepName: string, documentName: string, data: any) => {
    return { currentStep: stepName, currentPage: documentName, data };
  };

  const handleRejectedDocument = async (values: any, stepName: string) => {
    const rejectedDocument = documentationModel.documents?.find(
      (i) => i.userDocument?.additionalData?.status === 'REJECTED',
    );
    if (rejectedDocument) {
      const data = { currentStep: stepName, currentPage: rejectedDocument.name };
      await aamBackendApi.removeDocument(
        clientOrganizationId!,
        selectedConfigurationId!,
        userId!,
        applicationId!,
        data,
      );
    }
  };

  const prepareFilesAndData = (values: any) => {
    const partsNames = document.parts.map((part) => part.name);
    const files: any = {};
    const data: any = {};
    for (const [name, value] of Object.entries(values)) {
      if (partsNames.includes(name)) {
        files[name] = state[name].file;
      } else {
        data[name] = value;
      }
    }
    OOFlowWrapper.prepareValues(document.controls, data);
    return { files, data };
  };

  const handleSubmit = async (sendData: any, files: any) => {
    await mutateAsync({ data: sendData, files });
    await callOnSubmit(!isLastDocumentToUpload && documentation.isMultipleDocumentsEnabled ? documentation : undefined);
  };

  const handleDcImageSubmit = async () => {
    setFw(false);
    try {
      const sendData = {
        currentStep: stepName,
        currentPage: document.name,
        data: { dcDocumentType, dcAdditionalDocumentType },
      };
      const files: any = {};
      sdkCapturedDocument?.images.forEach((rawBase64: string, index: number) => {
        const fileName = `${document?.name}_${dcDocumentParts[index]}`;
        const blob = base64ToBlob(rawBase64, 'image/jpeg');
        const file = new File([blob], `${fileName}.jpg`, {
          type: blob.type,
        });
        setState({ ...state, [fileName]: { ...state[fileName], file } });
        files[fileName] = file;
      });

      await mutateAsync({ data: sendData, files });
    } catch (err) {
      dispatch(
        snackbarSlice.actions.showError({
          message: err?.data?.message ?? 'failed to save captured document',
        }),
      );
    }

    await callOnSubmit(!isLastDocumentToUpload && documentation.isMultipleDocumentsEnabled ? documentation : undefined);
    setFw(true);
  };

  const handleErrors = (err: any, actions: any) => {
    const parsed = parseValidationErrors(t, err);

    if (parsed.errors) {
      const controlStatus: Record<string, any> = {};
      Object.keys(parsed.errors).forEach((x) => {
        let prefix = parsed.errors[x]?.split('.')?.[0].toLowerCase() || '';
        const prefixAlias: any = {
          documents2: 'documents',
          documents3: 'documents',
        };
        if (prefixAlias[prefix]) {
          prefix = prefixAlias[prefix];
        }
        controlStatus[x] = t(`${prefix}:${parsed.errors[x]}`) || '';
      });
      actions.setErrors(controlStatus);
    } else {
      dispatch(
        snackbarSlice.actions.showError({
          message: t(`documents:validations.invalid_pdf`) ?? 'An error occurred while submitting the document',
        }),
      );
    }
  };

  if (isReadOnly && isVisibleReadOnly) {
    return (
      <ReadOnlyDocumentInfo
        document={document}
        state={state}
        documentationSelected={documentationSelected}
        setDocument={setDocument}
        nextButtonClicked={nextButtonClicked}
      />
    );
  }

  if (!fw) {
    return <Loader />;
  }
  return (
    <>
      <Box m={{ md: 2 }}>
        {getStepperComponent(themeAdditionalData.stepper)}
        {['papercapture', 'autocapture'].includes(sdkMode) ? (
          <OpenSdkCameraDisclaimer
            setSdkSuccess={setSdkSuccess}
            setSdkError={setSdkError}
            setSdkCapturedDocument={setSdkCapturedDocument}
            dcAllowedDocument={dcAllowedDocument}
            sdkMode={sdkMode}
          />
        ) : (
          <Formik
            enableReinitialize={true}
            validateOnMount={true}
            validateOnChange={true}
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={async (values, actions) => {
              try {
                await handleRejectedDocument(values, stepName);
                const { data, files } = prepareFilesAndData(values);
                const sendData = getSendData(
                  documentationModel.uploadEarly ? OOStepsEnum.documentUploadTwo : stepName,
                  document.name,
                  data,
                );
                await handleSubmit(sendData, files);
              } catch (err) {
                handleErrors(err, actions);
              }
            }}
          >
            {(props) => (
              <form encType="multipart/form-data" onSubmit={props.handleSubmit} style={{ flexGrow: 1 }}>
                <Box
                  mb={2}
                  display="flex"
                  flexDirection="column"
                  alignItems="stretch"
                  justifyContent="space-between"
                  minHeight="80vh"
                >
                  <Box>
                    <Grid container spacing={3}>
                      <Grid item xs={12}>
                        {cameraActive && (
                          <Camera
                            onCapture={(blob) => cameraPictureTaken(blob, props)}
                            defaultFacingMode="environment"
                          />
                        )}
                      </Grid>
                    </Grid>
                    {!cameraActive && (
                      <Grid container spacing={3}>
                        <Grid item xs={12}>
                          <h3 style={{ ...themeAdditionalData.smallTitle }}>
                            {stepLabel} - {t(`documents:${document.label}`)}
                          </h3>
                        </Grid>
                        {document.parts.map((part) => (
                          <React.Fragment key={part.name}>
                            {!hideDocumentExamples && (
                              <ExampleDocument
                                state={state}
                                part={part}
                                setPreviewImageUrl={setPreviewImageUrl}
                                previewImageUrl={previewImageUrl}
                              />
                            )}

                            <Grid item xs={12}>
                              <Grid container direction="column" key={part.name}>
                                <Grid>
                                  {t(`documents:${part.description}`) && (
                                    <Box mb={2}>
                                      <Typography>{t(`documents:${part.description}`)}</Typography>
                                    </Box>
                                  )}
                                </Grid>
                                <Grid container item direction="row" spacing={1}>
                                  <Grid item xs={12}>
                                    <Typography style={{ textTransform: 'uppercase' }}>
                                      {t(`documents:${part.label}`)} {part.isMandatory ? '*' : ''}
                                    </Typography>
                                  </Grid>

                                  <Grid item sm={12} md={12} xs={12}>
                                    <label
                                      htmlFor={`btn-upload-${part.name}`}
                                      className="document-form__action-button-special document-upload-label"
                                    >
                                      {!isRemoving && (
                                        <FileUpload
                                          props={props}
                                          part={part}
                                          controls={document.controls}
                                          onInvalidFileChoose={onInvalidFileChoose}
                                          validateFile={validateFile}
                                          setState={setState}
                                          validateField={true}
                                          setIsFileReuploaded={setIsFileReuploaded}
                                        />
                                      )}
                                      <span
                                        className="tag-ds button-secondary"
                                        onClick={() => setPartName(part.name)}
                                        style={{
                                          width: '100%',
                                          color: themeAdditionalData?.smallTitle?.color,
                                        }}
                                      >
                                        {t('candidate_recruiter:DOCUMENTS.DOCUMENT_UPLOAD.addFiles').toUpperCase()}
                                        <span className="material-icons right">file_upload</span>
                                      </span>
                                    </label>
                                  </Grid>
                                  {!documentation.hideTakePhotoButton && (
                                    <Grid item sm={12} md={12} xs={12}>
                                      <div className="document-form__action-button-special document-upload-label">
                                        <BoxedButton
                                          component="span"
                                          variant="text"
                                          color="primary"
                                          fullWidth={false}
                                          onClick={() => {
                                            setCameraPart(part.name);
                                            setCameraActive(true);
                                          }}
                                          text={t('candidate_recruiter:DOCUMENTS.DOCUMENT_UPLOAD.takePhoto')}
                                          style={{
                                            color: themeAdditionalData?.smallTitle?.color ?? theme.palette.info.dark,
                                          }}
                                        />
                                      </div>
                                    </Grid>
                                  )}
                                </Grid>
                              </Grid>
                            </Grid>
                          </React.Fragment>
                        ))}
                        {areControlsVisible &&
                          allRequiredFilesUploaded &&
                          document.controls
                            .filter((c) => c.isVisibleCandidate)
                            .map((control) => (
                              <Grid xs={12} item key={control.name}>
                                <OOFormControl control={control} />
                              </Grid>
                            ))}
                      </Grid>
                    )}
                  </Box>
                  <Box py={2}>
                    <Box pt={1}>
                      <Button
                        variant="outlined"
                        color="primary"
                        disabled={onRemoveDisabled}
                        onClick={onRemoveDocuments}
                      >
                        {t('candidate_recruiter:DOCUMENTS.DOCUMENT_UPLOAD.removeDocument')}
                      </Button>
                    </Box>
                    <Box pt={1}>
                      <LoadingButton
                        variant="contained"
                        color="primary"
                        type="submit"
                        disabled={!props.isValid || isPending || rest.isLoading}
                        loading={isPending || rest.isLoading}
                      >
                        {t('candidate_recruiter:DOCUMENTS.DOCUMENT_UPLOAD.continue')}
                      </LoadingButton>
                    </Box>
                  </Box>
                </Box>
              </form>
            )}
          </Formik>
        )}
        <Box className="document-preview-dialog__container">
          <Dialog
            open={sizeError || typeError || emptyFileError}
            onClose={onInvalidFilePopupClose}
            disablePortal
            className="dialog-cenetered"
          >
            <ClosePageIcon onClick={onInvalidFilePopupClose} className="document-preview-dialog__close_icon" />
            {sizeError && (
              <Typography className="document-form__size-limit-error">
                {t('candidate_recruiter:DOCUMENTS.DOCUMENT_UPLOAD.sizeLimit', { sizeLimit })}
              </Typography>
            )}
            {emptyFileError && (
              <Typography className="document-form__size-limit-error">
                {t('candidate_recruiter:DOCUMENTS.DOCUMENT_UPLOAD.emptyFileError')}
              </Typography>
            )}
            {typeError && (
              <Typography className="document-form__size-limit-error">
                {t('candidate_recruiter:DOCUMENTS.DOCUMENT_UPLOAD.typeLimit')}
              </Typography>
            )}
          </Dialog>
        </Box>
      </Box>
      {Object.keys(state).length > 0 && (
        <Box m={{ md: 2 }}>
          <Box
            mt="10px"
            mb="10px"
            style={{
              display: 'grid',
              justifyItems: 'end',
            }}
          >
            <IconButton onClick={() => onRemoveDocuments()} size="large">
              <CloseIcon />
            </IconButton>
          </Box>
          <Box mb="24px">
            <Typography variant="h2" style={{ textTransform: 'uppercase' }}>
              {t(`documents:${document.label}`)}
            </Typography>
          </Box>
          <div
            style={{
              display: 'grid',
              gap: '1rem',
              gridAutoFlow: 'column',
              gridAutoColumns: 'minmax(0, 1fr)',
            }}
          >
            {sdkMode
              ? document?.userDocument?.userDocumentParts?.map((part, index) => (
                  <React.Fragment key={index}>
                    {state[part.name]?.data && (
                      <Grid>
                        <ImagePreview state={state} part={part} />
                      </Grid>
                    )}
                  </React.Fragment>
                ))
              : document?.parts?.map((part, index) => (
                  <React.Fragment key={index}>
                    {state[part.name]?.data && (
                      <Grid>
                        <ImagePreview state={state} part={part} />
                      </Grid>
                    )}
                  </React.Fragment>
                ))}
          </div>
        </Box>
      )}
      <SuccessPageDialog
        isVisible={sdkSuccess}
        setIsDataCheckerSuccess={setSdkSuccess}
        handleSubmit={handleDcImageSubmit}
      />
      <ErrorPageDialog isVisible={sdkError} setIsDataCheckerError={setSdkError} />
    </>
  );
};
